// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { t, TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabel, BaseSelect, BaseTable, Loading } from '~/components/common';
// Others
import {
  IQueryParamListExport,
  IRevenueMonthReport,
  IRevenueMonthViewResult,
  IRevenueMonthViewRow,
  IRevenueWeekReport,
} from '~/utils/interface/reports';
import { useAppDispatch } from '~/utils/redux/hooks';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { ReportTypeEnum, ReportViewEnum } from '~/utils/enum/common';
import { exportDataReport, getListRevenueReport } from '~/thunks/reports/reportsThunk';
import { IBaseOption, IHeaderCellType, IResultCallApiFn } from '~/utils/interface/common';
import { BACK_TO_PREVIOUS_PAGE, colorsRevenueReport, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './RevenueReport.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columnsWeek = (t: TFunction): IHeaderCellType<IRevenueWeekReport>[] => [
  {
    id: 'weekPeriod',
    label: <Box>{t('report_table_week_period')}</Box>,
    render: (record) => (
      <Stack direction='row' spacing={3} alignItems='center'>
        <Box width={12} height={12} borderRadius={6} bgcolor={record.color} />
        <Typography className={cx('weekPeriod')}>{record?.weekPeriod}</Typography>
      </Stack>
    ),
  },
  {
    id: 'orderTotal',
    label: <Box>{t('report_table_number_of_orders')}</Box>,
    render: (record) => <Typography className={cx('textValue')}>{record?.orderTotal}</Typography>,
  },
  {
    id: 'amountPaid',
    label: <Box>{t('report_table_revenue')}</Box>,
    render: (record) => (
      <Typography className={cx('textValue')}>
        {formatCurrencyUSD({ value: record?.amountPaid, showDecimal: true })}
      </Typography>
    ),
  },
  {
    id: 'amountTotal',
    label: <Box>{t('report_table_potential_revenue')}</Box>,
    render: (record) => (
      <Typography className={cx('textValue')}>
        {formatCurrencyUSD({ value: record?.amountTotal, showDecimal: true })}
      </Typography>
    ),
  },
];

const columnsMonth = (years: string[]): IHeaderCellType<IRevenueMonthViewRow>[] => {
  const baseColumns: IHeaderCellType<IRevenueMonthViewRow>[] = [
    {
      id: 'month',
      label: t('common_text_month', { ns: 'common/base' }),
      render: (record) => (
        <Stack direction='row' spacing={2} alignItems='center'>
          <Box width={10} height={10} borderRadius='50%' bgcolor={record.color} />
          <Typography className={cx('monthPeriod')}>{record.month}</Typography>
        </Stack>
      ),
    },
  ];

  const yearColumns: IHeaderCellType<IRevenueMonthViewRow>[] = years.map((year) => ({
    id: year,
    label: year,
    render: (record) => {
      const value = record[year];
      return (
        <Typography className={cx('textValue')}>
          {formatCurrencyUSD({ value: value?.toString(), showDecimal: true })}
        </Typography>
      );
    },
  }));

  return [...baseColumns, ...yearColumns];
};

const RevenueReport = () => {
  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [yearNumber, setYearNumber] = useState<string>(EMPTY_STRING);
  const [revenueWeekList, setRevenueWeekList] = useState<IRevenueWeekReport[]>([]);
  const [revenueMonthList, setRevenueMonthList] = useState<IRevenueMonthViewRow[]>([]);
  const [yearList, setYearList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    view: ReportViewEnum.WEEK,
  });
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListRevenueReport();
  }, [paramsObject]);

  useEffect(() => {
    if (!yearList) return;

    setParamsObject({
      ...paramsObject,
      ...(yearNumber ? { year: yearNumber } : { year: undefined }),
    });
  }, [yearNumber]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListRevenueReport = () => {
    setIsLoading(true);
    dispatch(getListRevenueReport(paramsObject))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        if (paramsObject?.view === ReportViewEnum.WEEK) {
          setRevenueWeekList(convertToWeekPeriod(data as IRevenueWeekReport[]));
        } else if (paramsObject?.view === ReportViewEnum.MONTH) {
          const { formattedData, years } = convertToMonthViewData(data as IRevenueMonthReport[]);
          setRevenueMonthList(formattedData);
          setYearList(years);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const convertToWeekPeriod = (data: IRevenueWeekReport[]): IRevenueWeekReport[] => {
    return data.map((item, index) => {
      const startDate = moment(item?.weekStart);
      const endDate = moment(item?.weekEnd);

      const weekNumber = startDate.isoWeek();

      const sameMonth = startDate.month() === endDate.month();
      const period = sameMonth
        ? `${startDate.format('MMM D')} - ${endDate.format('D')}`
        : `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;

      const color = colorsRevenueReport?.[index % colorsRevenueReport?.length];

      return {
        ...item,
        weekPeriod: `Week ${weekNumber} (${period})`,
        color,
      };
    });
  };

  const convertToMonthViewData = (data: IRevenueMonthReport[]): IRevenueMonthViewResult => {
    const monthNames = moment.months();

    const years = data.map((d) => d?.year);

    const formattedData: IRevenueMonthViewRow[] = monthNames?.map((month, index) => {
      const monthNumber = index + 1;

      const color = colorsRevenueReport?.[index % colorsRevenueReport?.length];

      const row: IRevenueMonthViewRow = { month, color };

      data.forEach((yearObj) => {
        const found = yearObj?.revenue?.find((r) => Number(r?.month) === monthNumber);
        row[yearObj?.year] = found ? found?.amountPaid : null;
      });

      return row;
    });

    return { formattedData, years };
  };

  const handleChangeView = (view: ReportViewEnum) => {
    if (view === paramsObject.view) return;
    setParamsObject({ ...paramsObject, view });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: paramsObject.view === ReportViewEnum.WEEK ? ReportTypeEnum.REVENUE_WEEK : ReportTypeEnum.REVENUE_MONTH,
      view: undefined,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const fetchOptionsYear = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const startYear = 2021;
    const currentYear = moment().year();

    const option = Array.from({ length: currentYear - startYear + 1 }, (_, index) => {
      const year = currentYear - index;
      return {
        value: year.toString(),
        label: year.toString(),
      };
    });
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: option,
      hasMoreResp: false,
    };

    return result;
  }, []);

  //#endregion Handle Function

  return (
    <Stack id='revenueReportPage' className={cx('container')} spacing={6}>
      <Stack direction='row' justifyContent='space-between'>
        <Stack direction='row' spacing={2} alignItems='center'>
          <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
            <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
          </IconButton>

          <Stack direction='column' spacing={2}>
            <BaseLabel label={t('reports_revenue_title')} styleType='bigTitle' />
            <Typography className={cx('description')}>{t('reports_revenue_desc')}</Typography>
          </Stack>
        </Stack>

        <Stack direction='row' spacing={4} alignItems='center'>
          <BaseSelect
            width={150}
            styleType='contained'
            placeholder={t('reports_revenue_title_select_year')}
            value={yearNumber}
            callApiFn={fetchOptionsYear}
            onChange={(option) => setYearNumber(option?.value || EMPTY_STRING)}
          />

          <Stack direction='row' spacing='2px' p={1} className={cx('viewAction')}>
            <Button
              variant='contained'
              className={cx('buttonAction', { active: paramsObject.view === ReportViewEnum.WEEK })}
              onClick={() => handleChangeView(ReportViewEnum.WEEK)}
            >
              {t('common_text_weekly', { ns: 'common/base' })}
            </Button>
            <Button
              variant='contained'
              className={cx('buttonAction', { active: paramsObject.view === ReportViewEnum.MONTH })}
              onClick={() => handleChangeView(ReportViewEnum.MONTH)}
            >
              {t('common_text_monthly', { ns: 'common/base' })}
            </Button>
          </Stack>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            disabled={!revenueWeekList?.length && !revenueMonthList?.length}
            onClick={handleExport}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Stack className={cx('tableContainer')}>
        {paramsObject.view === ReportViewEnum.WEEK && (
          <BaseTable borderRadius={0} dataSource={revenueWeekList} columns={columnsWeek(t)} />
        )}

        {paramsObject.view === ReportViewEnum.MONTH && (
          <BaseTable borderRadius={0} dataSource={revenueMonthList} columns={columnsMonth(yearList)} />
        )}
      </Stack>

      {isLoading && <Loading />}
    </Stack>
  );
};

export default RevenueReport;
